import React from 'react';
import { Clock, MapPin, Phone } from 'lucide-react';

const ORDER_LINKS = [
  {
    id: 'ubereats',
    label: 'Order on UberEats',
    href: 'https://www.ubereats.com/store/ploys-thai-food/GrvjhdNHSASFzNxZ8SWrOg',
    gradient: 'from-[#1FAE5C] to-[#0E7A3B]',
    shadow: 'shadow-[0_8px_24px_rgba(31,174,92,0.35)]'
  },
  {
    id: 'grubhub',
    label: 'Order on GrubHub',
    href: 'https://www.grubhub.com/restaurant/ploys-thai-food-7309-sw-garden-home-rd-portland/1275122',
    gradient: 'from-[#F5881F] to-[#C55A00]',
    shadow: 'shadow-[0_8px_24px_rgba(245,136,31,0.35)]'
  },
  {
    id: 'doordash',
    label: 'Order on DoorDash',
    href: 'https://www.doordash.com/store/ploys-thai-food-portland-1009490/1516654/?srsltid=AfmBOop7MmCJIOC6yPLwC_lgAPwqrw8f6X9caIM1v4M6wzxpoEYgBLq_',
    gradient: 'from-[#EF3B2D] to-[#B71C13]',
    shadow: 'shadow-[0_8px_24px_rgba(239,59,45,0.35)]'
  }
];

const InfoColumn = ({ icon, label, value, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    <h3
      className="text-[#F5D06E] font-black uppercase tracking-[0.12em] text-lg sm:text-xl"
      style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
    >
      {label}
    </h3>
    <div className="flex items-start gap-2 text-[#F5E6C9] text-sm sm:text-base leading-relaxed">
      {icon && <span className="text-[#E8B945] mt-0.5 shrink-0">{icon}</span>}
      <span className="tracking-wide">{value}</span>
    </div>
  </div>
);

const Footer = ({ restaurantInfo }) => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative mt-16" data-testid="main-footer">
      {/* Top divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#E8B945]/60 to-transparent" />

      {/* Info strip: 4 columns on desktop, stacked on mobile */}
      <div className="px-5 sm:px-8 py-12 sm:py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
          <div className="space-y-3" data-testid="brand-column">
            <h2
              className="text-[#F5D06E] font-black uppercase tracking-wide text-2xl sm:text-3xl leading-tight"
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
            >
              Ploy's<br className="hidden sm:block" /> Thai Food
            </h2>
            <p
              className="text-[#E8B945]/80 italic tracking-[0.12em] uppercase text-xs sm:text-sm"
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
            >
              Authentic Thai Cuisine
            </p>
          </div>

          <InfoColumn
            label="Hours"
            value={restaurantInfo.hours.toUpperCase()}
            testId="footer-hours"
          />
          <InfoColumn
            icon={<MapPin className="w-4 h-4" />}
            label="Location"
            value={restaurantInfo.address.toUpperCase()}
            testId="footer-location"
          />
          <InfoColumn
            icon={<Phone className="w-4 h-4" />}
            label="Contact"
            value={`CALL US AT ${restaurantInfo.phone}`}
            testId="footer-contact"
          />
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-[#E8B945]/60 to-transparent" />

      {/* Order Online */}
      <div className="px-5 sm:px-8 py-14 text-center" id="order">
        <h2
          className="text-[#F5D06E] font-black uppercase tracking-[0.1em] mb-10"
          style={{
            fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)'
          }}
          data-testid="order-online-heading"
        >
          Order Online
        </h2>
        <div className="flex flex-col sm:flex-row justify-center gap-5 sm:gap-6 max-w-4xl mx-auto">
          {ORDER_LINKS.map((link) => (
            <a
              key={link.id}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 rounded-2xl bg-gradient-to-b ${link.gradient} ${link.shadow} text-white font-black uppercase tracking-widest text-base sm:text-lg py-6 sm:py-8 px-4 hover:scale-[1.03] hover:brightness-110 active:scale-[0.98] transition-all duration-200`}
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif", letterSpacing: '0.08em' }}
              data-testid={`order-link-${link.id}`}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-[#E8B945]/60 to-transparent" />

      {/* Copyright */}
      <div className="px-5 py-8 text-center">
        <p
          className="text-[#F5D06E] uppercase tracking-[0.15em] text-xs sm:text-sm"
          style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
          data-testid="footer-copyright"
        >
          © {year} Ploy's Thai Food. All rights reserved.
        </p>
      </div>

      {/* Studio credit */}
      <a
        href="https://studioat.earth/"
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center pb-12 pt-2 group"
        data-testid="studio-credit"
      >
        <p className="mb-2">
          <span
            className="text-[#F5E6C9]/90 italic text-2xl sm:text-3xl tracking-wide"
            style={{ fontFamily: "'Great Vibes', 'Pinyon Script', cursive" }}
          >
            Website by{' '}
          </span>
          <span
            className="text-[#FFD60A] font-black text-2xl sm:text-3xl tracking-wider align-middle"
            style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
          >
            STUDIOAT
          </span>
        </p>
        <p
          className="text-[#F5E6C9]/80 uppercase tracking-[0.25em] text-xs sm:text-sm group-hover:text-[#F5D06E] transition-colors"
          style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
        >
          www.studioat.earth
        </p>
      </a>
    </footer>
  );
};

export default Footer;
